import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import React, { useState } from "react";
import { Input, Select, DatePicker, Switch, Tooltip } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { CampaignData } from "@/api/campaign";
import { CampaignStatusPhase, CampaignType, getStatusTypeFromPhase } from "@/api/campaign";
import type { CampaignStatusType } from "@/api/campaign";
import type { BriefGuidelineSection } from "@/api/campaignBrief";
import { CURRENCY_OPTIONS } from "@/shared/utils/currency";
import { Icon } from "@/shared/components/Icon/Icon";
import type { CampaignBriefFields } from "../../hooks/useCampaignBrief";
import { DEFAULT_CAMPAIGN_DONTS, DEFAULT_CAMPAIGN_DOS, isLockedDefault } from "../../utils/briefDefaults";

const { TextArea } = Input;

interface Props {
	campaign: CampaignData | null;
	canEdit: boolean;
	/** Admins and the campaign's agency team can switch its Campaign Engagement Terms (CET) flow on or off. */
	canToggleCet?: boolean;
	/** CET is fixed once the first creator is in Content Execution. */
	cetLocked?: boolean;
	coreState: {
		name: string;
		projectCode: string;
		clientStr: string;
		status: CampaignStatusType;
		type: CampaignType;
		statusPhase: CampaignStatusPhase;
		totalBudget?: string;
		currency?: string;
		cetEnabled?: boolean;
		canSave: boolean;
	};
	coreActions: {
		setName: (value: string) => void;
		setProjectCode: (value: string) => void;
		setClientStr: (value: string) => void;
		setType: (value: CampaignType) => void;
		setStatus: (value: CampaignStatusType) => void;
		setStatusPhase: (value: CampaignStatusPhase) => void;
		setTotalBudget: (value: string) => void;
		setCurrency: (value: string) => void;
		setCetEnabled?: (value: boolean) => void;
	};
	fields: CampaignBriefFields;
	setField: <K extends keyof CampaignBriefFields>(key: K, value: CampaignBriefFields[K]) => void;
	standardGuidelines: BriefGuidelineSection[];
}

// ---- shared styles (tuned to match the design) ----
const COLORS = {
	title: "#1A1A1A",
	label: "#6B7280",
	hint: "#9CA3AF",
	border: "#E5E7EB",
	softBorder: "#EEEEF1",
	divider: "#ECECEF",
	headerBg: "#F1F1F4",
	itemBg: "#F5F5F7",
	fieldBg: "#FFFFFF",
	link: "#4F46E5",
	primary: "#3A0CA3",
	softBg: "#F7F7F9",
	chipBg: "#EEF0FE",
};

const labelStyle: React.CSSProperties = { fontSize: 13, color: COLORS.label, marginBottom: 6 };
const sectionTitleStyle: React.CSSProperties = { fontSize: 16, fontWeight: 600, color: COLORS.title };
const sectionHintStyle: React.CSSProperties = { fontSize: 13, color: COLORS.hint, marginTop: 2, marginBottom: 14 };
const controlStyle: React.CSSProperties = { height: 44, borderRadius: 10, borderColor: COLORS.border };
const boxStyle: React.CSSProperties = { borderRadius: 10, borderColor: COLORS.border };

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div>
		<div style={labelStyle}>{label}</div>
		{children}
	</div>
);

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode; style?: React.CSSProperties }> = ({
	title,
	hint,
	children,
	style,
}) => (
	<div style={{ marginTop: 32, ...style }}>
		<div style={sectionTitleStyle}>{title}</div>
		{hint ? <div style={sectionHintStyle}>{hint}</div> : <div style={{ height: 14 }} />}
		{children}
	</div>
);

// Editable list of items (Do's / Don'ts): each item is a soft rounded row.
// The leading standard defaults (`lockedValues`) render read-only with no
// edit/delete affordance, even when canEdit is true; everything else stays editable.
const ListEditor: React.FC<{
	items: string[];
	canEdit: boolean;
	heading: string;
	placeholder: string;
	lockedValues: string[];
	onChange: (next: string[]) => void;
}> = ({ items, canEdit, heading, placeholder, lockedValues, onChange }) => {
	const update = (idx: number, value: string) => {
		const next = [...items];
		next[idx] = value;
		onChange(next);
	};
	const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
	const add = () => onChange([...items, ""]);

	return (
		<div style={{ borderRadius: 12, overflow: "hidden", background: "#F3F3F3" }}>
			<div style={{ padding: "12px 16px", fontWeight: 600, fontSize: 14, color: "#374151" }}>{heading}</div>
			<div style={{ padding: "0 12px 12px" }}>
				{items.map((item, idx) => {
					const editable = canEdit && !isLockedDefault(items, idx, lockedValues);
					return (
						<div
							key={idx}
							style={{
								display: "flex",
								alignItems: "flex-start",
								gap: 8,
								background: "#E6E6E6B0",
								borderRadius: 8,
								padding: editable ? "8px 6px 8px 12px" : "11px 14px",
								marginBottom: 8,
							}}
						>
							{editable ? (
								<>
									<TextArea
										variant="borderless"
										autoSize
										className="brief-item-input"
										value={item}
										placeholder={placeholder}
										onChange={(e) => update(idx, e.target.value)}
										style={{
											fontSize: 14,
											color: "#374151",
											background: "transparent",
											resize: "none",
										}}
									/>
									<button
										type="button"
										aria-label="Remove item"
										onClick={() => remove(idx)}
										style={{
											flexShrink: 0,
											height: 21,
											width: 24,
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											background: "none",
											border: "none",
											color: "#9CA3AF",
											cursor: "pointer",
											fontSize: 16,
											padding: 0,
										}}
									>
										<CloseOutlined />
									</button>
								</>
							) : (
								<span style={{ fontSize: 14, color: "#374151" }}>{item}</span>
							)}
						</div>
					);
				})}
				{canEdit ? (
					<button
						type="button"
						onClick={add}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							background: "none",
							border: "none",
							color: COLORS.primary,
							fontSize: 12,
							lineHeight: "18px",
							fontWeight: 500,
							cursor: "pointer",
							padding: "6px 4px",
						}}
					>
						<PlusOutlined style={{ fontSize: 16 }} /> Add item
					</button>
				) : null}
			</div>
		</div>
	);
};

// Hashtag chips + a dashed "Type to add" pill.
const TagsEditor: React.FC<{ tags: string[]; canEdit: boolean; onChange: (next: string[]) => void }> = ({
	tags,
	canEdit,
	onChange,
}) => {
	const [draft, setDraft] = useState("");
	const commit = () => {
		const v = draft.trim();
		if (v && !tags.includes(v)) onChange([...tags, v]);
		setDraft("");
	};
	return (
		<div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
			{tags.map((t) => (
				<span
					key={t}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 8,
						background: COLORS.chipBg,
						color: "#3730A3",
						borderRadius: 18,
						padding: "6px 14px",
						fontSize: 14,
					}}
				>
					{t}
					{canEdit ? (
						<button
							type="button"
							aria-label={`Remove ${t}`}
							onClick={() => onChange(tags.filter((x) => x !== t))}
							style={{
								display: "inline-flex",
								alignItems: "center",
								background: "none",
								border: "none",
								fontSize: 11,
								cursor: "pointer",
								color: "#6366F1",
								padding: 0,
							}}
						>
							<CloseOutlined />
						</button>
					) : null}
				</span>
			))}
			{canEdit ? (
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						border: `1px dashed ${COLORS.border}`,
						borderRadius: 18,
						padding: "2px 12px",
					}}
				>
					<Input
						variant="borderless"
						value={draft}
						placeholder="Type to add"
						onChange={(e) => setDraft(e.target.value)}
						onPressEnter={commit}
						onBlur={commit}
						style={{ width: 110, padding: 0, fontSize: 14 }}
					/>
					<PlusOutlined style={{ color: "#9CA3AF", fontSize: 12 }} />
				</span>
			) : null}
		</div>
	);
};

const toDayjs = (v: string | null) => (v ? dayjs(v) : null);

const CET_LOCKED_HINT = "Can't be changed after a creator is added to Content Execution.";

export const CampaignBriefTab: React.FC<Props> = ({
	canEdit,
	canToggleCet = false,
	cetLocked = false,
	coreState,
	coreActions,
	fields,
	setField,
	standardGuidelines,
}) => {
	const ro = { readOnly: !canEdit };
	const textArea = (rows: number) => ({
		autoSize: { minRows: rows, maxRows: rows + 8 },
		readOnly: !canEdit,
		style: boxStyle,
	});

	const onBudget = (e: React.ChangeEvent<HTMLInputElement>) => coreActions.setTotalBudget(e.target.value);

	return (
		<div>
			{/* ---- Campaign Details card (core fields) ---- */}
			<div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 24 }}>
				<div style={sectionTitleStyle}>Campaign Details</div>
				<div style={sectionHintStyle}>
					Briefly describe the campaign: what are you trying to achieve, and who is it for?
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
					<Field label="Campaign Name">
						<Input
							value={coreState.name}
							onChange={(e) => coreActions.setName(e.target.value)}
							status={canEdit && coreState.name.trim().length === 0 ? "error" : undefined}
							{...ro}
							style={controlStyle}
						/>
					</Field>
					<Field label="Brand Name">
						<Input
							value={coreState.clientStr}
							placeholder={canEdit ? "Enter brand name" : undefined}
							onChange={(e) => coreActions.setClientStr(e.target.value)}
							{...ro}
							style={controlStyle}
						/>
					</Field>
					<Field label="Status">
						<Select
							className="brief-control-select"
							disabled={!canEdit}
							value={coreState.statusPhase}
							onChange={(v) => {
								// Keep the To-do / In Progress / Completed status in step with the
								// phase, the same way the create campaign modal does.
								const phase = v as CampaignStatusPhase;
								coreActions.setStatusPhase(phase);
								coreActions.setStatus(getStatusTypeFromPhase(phase));
							}}
							options={Object.values(CampaignStatusPhase).map((v) => ({ label: v, value: v }))}
							popupMatchSelectWidth={false}
							style={{ width: "100%", height: 44 }}
						/>
					</Field>
				</div>

				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.3fr 0.8fr", gap: 16 }}>
					<Field label="Project Code">
						<Input
							value={coreState.projectCode}
							onChange={(e) => coreActions.setProjectCode(e.target.value)}
							{...ro}
							style={controlStyle}
						/>
					</Field>
					<Field label="Type">
						<Select
							className="brief-control-select"
							disabled={!canEdit}
							value={coreState.type}
							onChange={(v) => coreActions.setType(v as CampaignType)}
							options={Object.values(CampaignType).map((v) => ({ label: v, value: v }))}
							popupMatchSelectWidth={false}
							style={{ width: "100%", height: 44 }}
						/>
					</Field>
					<Field label="Total Budget">
						<Input
							inputMode="decimal"
							value={coreState.totalBudget}
							onChange={onBudget}
							{...ro}
							style={controlStyle}
						/>
					</Field>
					<Field label="&nbsp;">
						<Select
							className="brief-control-select"
							showSearch
							disabled={!canEdit}
							value={coreState.currency || "USD"}
							onChange={(v) => coreActions.setCurrency(v as string)}
							options={CURRENCY_OPTIONS}
							optionLabelProp="label"
							optionFilterProp="value"
							popupMatchSelectWidth={false}
							style={{ width: "100%", height: 44 }}
						/>
					</Field>
				</div>

				{canToggleCet && coreActions.setCetEnabled && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 16,
							marginTop: 16,
							paddingTop: 16,
							borderTop: `1px solid ${COLORS.border}`,
						}}
					>
						<div>
							<div style={labelStyle}>Campaign Engagement Terms (CET)</div>
							<div style={sectionHintStyle}>
								{coreState.cetEnabled
									? "Creators must accept the Engagement Terms before they can be approved and paid."
									: "Engagement Terms are skipped. Creators can be approved and paid without accepting them."}
							</div>
						</div>
						<Tooltip title={cetLocked ? CET_LOCKED_HINT : undefined}>
							{/* The span lets the tooltip show while the switch is disabled. */}
							<span
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 10,
									cursor: cetLocked ? "not-allowed" : undefined,
								}}
							>
								{/* Explicit state beside the switch so On/Off reads at a glance. */}
								<span
									aria-hidden="true"
									style={{
										fontSize: 14,
										fontWeight: 600,
										color: coreState.cetEnabled ? CUSTOM_COLORS.PINK : COLORS.label,
									}}
								>
									{coreState.cetEnabled ? "On" : "Off"}
								</span>
								<Switch
									aria-label="Campaign Engagement Terms (CET)"
									checked={Boolean(coreState.cetEnabled)}
									disabled={cetLocked}
									onChange={(checked) => coreActions.setCetEnabled?.(checked)}
									style={coreState.cetEnabled ? { backgroundColor: CUSTOM_COLORS.PINK } : undefined}
								/>
							</span>
						</Tooltip>
					</div>
				)}
			</div>

			{/* ---- Timeline ---- */}
			<Section title="Timeline" hint="Key production and go-live milestones">
				<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
					{(
						[
							["Concept Due", "conceptDueDate"],
							["Video Assets Due", "videoAssetsDueDate"],
							["Go Live", "goLiveDate"],
						] as const
					).map(([label, key]) => (
						<Field key={key} label={label}>
							<DatePicker
								value={toDayjs(fields[key])}
								disabled={!canEdit}
								format="MMM DD, YYYY"
								allowClear
								suffixIcon={<Icon src="/icon/calendar.svg" size={18} color="#9CA3AF" />}
								style={{ width: "100%", ...controlStyle }}
								onChange={(d) => setField(key, d ? d.format("YYYY-MM-DD") : null)}
							/>
						</Field>
					))}
				</div>
			</Section>

			<Section title="About the Campaign" hint="The story and context creators need to represent">
				<TextArea
					value={fields.about ?? ""}
					onChange={(e) => setField("about", e.target.value)}
					{...textArea(2)}
				/>
			</Section>

			<Section title="Campaign Objective" hint="What success looks like for this campaign">
				<TextArea
					value={fields.objective ?? ""}
					onChange={(e) => setField("objective", e.target.value)}
					{...textArea(2)}
				/>
			</Section>

			<Section title="Key Messages" hint="The lines you want creators echoing in their content">
				<TextArea
					value={fields.keyMessages ?? ""}
					onChange={(e) => setField("keyMessages", e.target.value)}
					{...textArea(3)}
				/>
			</Section>

			<Section title="Creative Direction" hint="Content pillars creators can choose from">
				<TextArea
					value={fields.creativeDirection ?? ""}
					onChange={(e) => setField("creativeDirection", e.target.value)}
					{...textArea(1)}
				/>
			</Section>

			<Section title="Video Format" hint="Required elements and suggested content formats">
				<div style={labelStyle}>Required Element</div>
				<Input
					value={fields.videoFormatRequiredElement ?? ""}
					onChange={(e) => setField("videoFormatRequiredElement", e.target.value)}
					{...ro}
					style={{ ...controlStyle, marginBottom: 16 }}
				/>
				<div style={labelStyle}>Suggested Formats</div>
				<TextArea
					value={fields.videoFormatSuggestedFormats ?? ""}
					onChange={(e) => setField("videoFormatSuggestedFormats", e.target.value)}
					{...textArea(3)}
				/>
			</Section>

			<Section title="Edit Guidelines" hint="General edit requirements for this campaign's content">
				<TextArea
					value={fields.editGuidelines ?? ""}
					onChange={(e) => setField("editGuidelines", e.target.value)}
					{...textArea(1)}
				/>
			</Section>

			<Section title="Do's & Don'ts" hint="Guardrails for how creators represent the campaign">
				<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
					<ListEditor
						items={fields.campaignDos}
						canEdit={canEdit}
						heading="Do"
						placeholder="Add a do"
						lockedValues={DEFAULT_CAMPAIGN_DOS}
						onChange={(next) => setField("campaignDos", next)}
					/>
					<ListEditor
						items={fields.campaignDonts}
						canEdit={canEdit}
						heading="Don't"
						placeholder="Add a don't"
						lockedValues={DEFAULT_CAMPAIGN_DONTS}
						onChange={(next) => setField("campaignDonts", next)}
					/>
				</div>
			</Section>

			{/* ---- Standard guidelines (read-only, fed from backend) ---- */}
			{standardGuidelines.length > 0 ? (
				<Section
					title="Edit Guidelines"
					hint="Music, safe zones, and approval requirements — standard across all briefs, not editable"
				>
					<div style={{ background: COLORS.softBg, borderRadius: 12, padding: "4px 24px" }}>
						{standardGuidelines.map((g, i) => (
							<div
								key={i}
								style={{
									display: "grid",
									gridTemplateColumns: "200px 1fr 1.2fr",
									gap: 24,
									padding: "20px 0",
									alignItems: "center",
								}}
							>
								<div style={{ fontWeight: 500, color: "#4B5563" }}>{g.title}</div>
								<div style={{ color: "#4B5563", fontSize: 14, lineHeight: 1.5 }}>{g.body}</div>
								<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
									{g.links.map((link) => (
										<a
											key={link}
											href={link}
											target="_blank"
											rel="noreferrer"
											style={{ color: COLORS.link, wordBreak: "break-all", fontSize: 14 }}
										>
											{link}
										</a>
									))}
								</div>
							</div>
						))}
					</div>
				</Section>
			) : null}

			<Section title="Call to Action" hint="What creators should tell their audience to do next">
				<Input
					value={fields.callToAction ?? ""}
					onChange={(e) => setField("callToAction", e.target.value)}
					{...ro}
					style={controlStyle}
				/>
			</Section>

			<Section title="Tag & Hashtags" hint="Required tags and hashtags for every post">
				<TagsEditor tags={fields.hashtags} canEdit={canEdit} onChange={(next) => setField("hashtags", next)} />
			</Section>

			<style>{`
				.brief-control-select .ant-select-selector {
					height: 44px !important;
					border-radius: 10px !important;
					display: flex !important;
					align-items: center !important;
				}
				textarea.brief-item-input.ant-input {
					padding: 0 !important;
					line-height: 21px !important;
					min-height: 21px !important;
					vertical-align: middle !important;
				}
			`}</style>
		</div>
	);
};
